import React from 'react';
import Collapse, { Panel } from 'rc-collapse';
import {
  LOCALIZATION_LANGUAGES,
  EMPTY_LOCALIZATIONS,
  FILLED_BACKGROUND,
  EMPTY_BACKGROUND,
} from './localizationLanguages';
import { LocalizationChangeHandler } from './LocalizationContainer';
import { TargetNode } from '../../models/TargetNode';

interface ContactField {
  id: string;
  label?: Record<string, string>;
}

interface ContactLocalizationPanelProps {
  node: TargetNode;
  // The contact/topic field's redisid — this panel edits that field's labels.
  // Unlike LocalizationContainer's numeric index, this one is a string id.
  localizationIndex: string;
  localizationChanged: LocalizationChangeHandler;
  header: React.ReactNode;
}

// NOTE: unlike its sibling this is NOT wrapped in React.memo, and it is rendered
// ~23 times in the contact-form panel plus 10 in the topic buttons. Memoizing it
// would be a behaviour change (it currently re-renders with the editor), so it
// is left as-is; worth revisiting if the editor ever feels slow.
const ContactLocalizationPanel = ({
  node, localizationIndex, localizationChanged, header,
}: ContactLocalizationPanelProps) => {
  // extra.k is a plain array of { id, label: { <lang>: text } } entries.
  const kList = node.extra?.k || [];
  const field = kList.find(item => item && item.id === localizationIndex);
  const stored = field && field.label && typeof field.label === 'object'
    ? field.label
    : EMPTY_LOCALIZATIONS;

  // Merged onto the empty baseline so every textarea has a string value.
  const values = { ...EMPTY_LOCALIZATIONS, ...stored };

  // Topic-button labels get two rows; contact fields one.
  const rows = localizationIndex.indexOf('topicButton_') > -1 ? 2 : 1;

  return (
    <Collapse accordion={true}>
      <Panel header={header}>
        {Object.entries(LOCALIZATION_LANGUAGES).map(([code, name]) => {
          const value = values[code] || '';
          return (
            <div key={code}>
              <input type="text" className="pure-u-4-24"
                style={{
                  minHeight: "24px",
                  // Was applied by mutating the created element's props, which
                  // React does not support; decided up front now. Same result.
                  background: value ? FILLED_BACKGROUND : EMPTY_BACKGROUND,
                  color: "rgba(255, 255, 255, 1)",
                }}
                value={`${code.toUpperCase()} ${name}`}
                name={`localization_${code}`}
                readOnly={true} />
              <textarea className="pure-u-20-24"
                rows={rows}
                style={{ resize: "none" }}
                value={value}
                onChange={localizationChanged}
                name={`localizationEdit_${code}`}
                data-redisid={localizationIndex}
                data-language={code} />
            </div>
          );
        })}
      </Panel>
    </Collapse>
  );
};

export default ContactLocalizationPanel;
